import React, { useEffect, useRef } from 'react';
import type { NormalizedCacheObject } from '@apollo/client';
import { ApolloClient, ApolloLink, ApolloProvider } from '@apollo/client';
import {
    getBrowserApolloClient,
    resetBrowserApolloClientStore,
} from '~/graphql/apollo-client';
import { createApolloCache } from '~/graphql/apollo-cache';
import { useRootData } from '@modules/root';
import { useUserLoggedIn } from '~/modules/user';

/**
 * Provides the Apollo Client to Remix components they can use Apollo client hooks.
 */
export function ApolloClientProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const { env } = useRootData();
    const { isLoggedIn } = useUserLoggedIn();
    const wasLoggedInRef = useRef(isLoggedIn);
    const clientRef = useRef<ApolloClient<NormalizedCacheObject>>();

    if (!clientRef.current) {
        clientRef.current =
            typeof window !== 'undefined'
                ? getBrowserApolloClient({ stage: env.stage })
                : createSsrApolloClient();
    }

    useEffect(() => {
        if (wasLoggedInRef.current !== isLoggedIn) {
            void resetBrowserApolloClientStore();
        }
        wasLoggedInRef.current = isLoggedIn;
    }, [isLoggedIn]);

    return (
        <ApolloProvider client={clientRef.current}>{children}</ApolloProvider>
    );
}

/**
 * Get a no-op Apollo Client for SSR.
 */
function createSsrApolloClient(): ApolloClient<NormalizedCacheObject> {
    return new ApolloClient({
        cache: createApolloCache(),
        link: ApolloLink.empty(),
    });
}
